import React, { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Plus, Trash2, QrCode, X, CheckCircle2, Circle, UserPlus, Share2, Check } from 'lucide-react';
import { api, apiError } from './api';

export default function OfficialsTab({ tid, tName }) {
  const [officials, setOfficials] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [cert, setCert] = useState('');
  const [qr, setQr] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const load = () => api.get(`/tournaments/${tid}/officials`).then((r) => setOfficials(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);

  const add = async () => {
    if (!name.trim()) return;
    setErr('');
    try { await api.post(`/tournaments/${tid}/officials`, { name, phone, cert_level: cert }); setName(''); setPhone(''); setCert(''); load(); }
    catch (e) { setErr(apiError(e)); }
  };
  const del = async (oid) => { await api.delete(`/officials/${oid}`); load(); };

  const autoAssign = async () => {
    setErr('');
    try {
      const { data } = await api.post(`/tournaments/${tid}/auto-assign-referees`);
      setMsg(`Assigned ${data.assigned} matches across ${data.referees} referee(s).`);
      load();
      setTimeout(() => setMsg(''), 4000);
    } catch (e) { setErr(apiError(e)); }
  };

  const inputCls = "bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 text-white text-sm focus:outline-none focus:border-[#00BFFF]";
  const checkinUrl = (token) => `${window.location.origin}/checkin/${token}`;
  const waText = (o) => encodeURIComponent(`Hi ${o.name}, you're officiating at ${tName || 'the tournament'}. Open your personal referee page to check in — all your matches are already loaded, just tap START MATCH:\n${checkinUrl(o.token)}`);
  const waHref = (o) => { const d = (o.phone || '').replace(/\D/g, ''); return d ? `https://wa.me/${d}?text=${waText(o)}` : `https://wa.me/?text=${waText(o)}`; };
  const copyLink = (o) => { navigator.clipboard.writeText(checkinUrl(o.token)); setCopied(true); setTimeout(() => setCopied(false), 1800); };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <input data-testid="official-name" className={`flex-1 min-w-[8rem] ${inputCls}`} placeholder="Official name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <input data-testid="official-phone" className={`w-36 ${inputCls}`} placeholder="Phone (for WhatsApp)" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <input className={`w-28 ${inputCls}`} placeholder="Cert level" value={cert} onChange={(e) => setCert(e.target.value)} />
        <button data-testid="add-official-btn" onClick={add} className="bg-[#00BFFF] text-black font-bold px-4 rounded-xl"><Plus size={18} /></button>
      </div>
      {err && <p className="text-[#FF3B30] text-sm">{err}</p>}
      <button data-testid="auto-assign-btn" onClick={autoAssign} className="w-full bg-[#1C1C1E] border border-[#00BFFF]/40 text-[#00BFFF] rounded-xl py-3 text-xs font-bold flex items-center justify-center gap-2">
        <UserPlus size={15} /> AUTO-ASSIGN REFEREES TO ALL FIXTURES
      </button>
      {msg && <p data-testid="auto-assign-msg" className="text-[#00D26A] text-sm text-center">{msg}</p>}
      <div className="space-y-2">
        {officials.length === 0 ? <p className="text-center text-gray-600 py-8 text-sm">No officials yet. Add referees & assistants.</p> :
          officials.map((o) => (
            <div key={o.id} data-testid={`official-${o.id}`} className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {o.checked_in ? <CheckCircle2 size={18} className="text-[#00D26A]" /> : <Circle size={18} className="text-gray-600" />}
                <div>
                  <p className="font-bold">{o.name}</p>
                  <p className="text-xs text-gray-500">{o.cert_level || 'Official'} • {o.checked_in ? 'Checked in' : 'Not checked in'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a data-testid={`wa-${o.id}`} href={waHref(o)} target="_blank" rel="noreferrer" title="Send matches via WhatsApp" className="text-[#25D366]"><Share2 size={18} /></a>
                <button data-testid={`qr-${o.id}`} onClick={() => setQr(o)} className="text-[#00BFFF]"><QrCode size={18} /></button>
                <button onClick={() => del(o.id)} className="text-gray-600 hover:text-[#FF3B30]"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
      </div>

      {qr && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setQr(null)}>
          <div className="bg-[#121212] border border-gray-800 rounded-3xl p-6 text-center max-w-xs w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold">Send matches to {qr.name}</h3>
              <button onClick={() => setQr(null)} className="text-gray-500"><X size={20} /></button>
            </div>
            <div className="bg-white p-4 rounded-2xl inline-block">
              <QRCodeCanvas value={checkinUrl(qr.token)} size={180} />
            </div>
            <p className="text-xs text-gray-500 mt-4">Referee scans this to load all their matches — they just tap START MATCH.</p>
            <div className="mt-5 space-y-2">
              <a data-testid="qr-whatsapp" href={waHref(qr)} target="_blank" rel="noreferrer"
                className="w-full bg-[#25D366] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"><Share2 size={16} /> Send on WhatsApp</a>
              <button data-testid="qr-copy" onClick={() => copyLink(qr)}
                className="w-full bg-[#1C1C1E] border border-gray-700 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2">
                {copied ? <><Check size={15} className="text-[#00D26A]" /> Copied</> : 'Copy link'}
              </button>
            </div>
            <p className="text-[10px] text-gray-600 mt-3 break-all">{checkinUrl(qr.token)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function AssignModal({ fixture, officials, onClose, onSaved }) {
  const [a, setA] = useState(fixture.assignments || { referee: null, ar1: null, ar2: null, fourth: null });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const roles = [['referee', 'Referee'], ['ar1', 'Assistant 1'], ['ar2', 'Assistant 2'], ['fourth', '4th Official']];

  const save = async () => {
    setErr(''); setBusy(true);
    try { await api.post(`/fixtures/${fixture.id}/assign`, a); onSaved(); }
    catch (e) { setErr(apiError(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-[#121212] border border-gray-800 rounded-3xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold flex items-center gap-2"><UserPlus size={18} /> Assign Officials</h2>
          <button data-testid="assign-close" onClick={onClose} className="text-gray-500"><X size={22} /></button>
        </div>
        <div className="space-y-3">
          {roles.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-400 w-28">{label}</span>
              <select data-testid={`assign-${key}`} value={a[key] || ''} onChange={(e) => setA({ ...a, [key]: e.target.value || null })}
                className="flex-1 bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 text-white text-sm">
                <option value="">— none —</option>
                {officials.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          ))}
        </div>
        {err && <p data-testid="assign-error" className="text-[#FF3B30] text-sm mt-3">{err}</p>}
        <button data-testid="assign-save" onClick={save} disabled={busy} className="w-full mt-5 bg-[#00BFFF] text-black font-bold py-3.5 rounded-xl disabled:opacity-50">
          {busy ? 'Saving…' : 'SAVE ASSIGNMENTS'}
        </button>
      </div>
    </div>
  );
}
